import type { HelpArticleSummary, HelpAudience, HelpBlock, HelpCategory } from '../services/helpCenterService';

/**
 * Hilfecenter-Navigation (30.09.2026) – REINE Logik ohne React:
 * Anker für Überschriften, Inhaltsverzeichnis eines Artikels, Artikel derselben
 * Kategorie (Seitennavigation) und Vor/Zurück-Nachbarn. Wird von
 * HelpArticlePage, HelpBlockRenderer und HelpCenterPage genutzt.
 */

export interface HelpTocEntry {
  id: string;
  text: string;
  level: 2 | 3;
}

/** Anker-Id aus Überschrift + Position (eindeutig, auch bei gleichen Texten). */
export const headingAnchorId = (text: string, index: number): string => {
  const base = (text || '')
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${base || 'abschnitt'}-${index + 1}`;
};

/** Inhaltsverzeichnis: alle Überschriften (H2 + H3) in Reihenfolge; die Id entspricht dem Anker im Renderer. */
export const buildToc = (blocks: HelpBlock[]): HelpTocEntry[] => {
  const entries: HelpTocEntry[] = [];
  blocks.forEach((block, index) => {
    if (block.type !== 'heading' || !block.text.trim()) return;
    entries.push({ id: headingAnchorId(block.text, index), text: block.text.trim(), level: block.level === 3 ? 3 : 2 });
  });
  return entries;
};

/** Artikel nach sortOrder, dann Anlage (Reihenfolge des Backends bleibt bei Gleichstand erhalten). */
export const sortArticles = (articles: HelpArticleSummary[]): HelpArticleSummary[] =>
  [...articles].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

/** Alle Artikel derselben Kategorie (sortiert) – ohne Kategorie: alle ohne (sichtbare) Kategorie. */
export const siblingArticles = (
  articles: HelpArticleSummary[],
  categoryId: string | null,
  categories: HelpCategory[] = []
): HelpArticleSummary[] => {
  const known = new Set(categories.map((c) => c.id));
  const inCategory = categoryId && known.has(categoryId)
    ? articles.filter((a) => a.categoryId === categoryId)
    : articles.filter((a) => !a.categoryId || !known.has(a.categoryId));
  return sortArticles(inCategory);
};

/** Vorheriger/nächster Artikel innerhalb einer sortierten Liste (null an den Rändern). */
export const prevNextArticle = (
  list: HelpArticleSummary[],
  slug: string
): { prev: HelpArticleSummary | null; next: HelpArticleSummary | null } => {
  const idx = list.findIndex((a) => a.slug === slug);
  if (idx < 0) return { prev: null, next: null };
  return { prev: idx > 0 ? list[idx - 1] : null, next: idx < list.length - 1 ? list[idx + 1] : null };
};

/** Kategorien + Artikel gruppieren (sichtbare Kategorien in Reihenfolge, Rest ans Ende). */
export const groupArticlesByCategory = (
  categories: HelpCategory[],
  articles: HelpArticleSummary[]
): { category: HelpCategory | null; articles: HelpArticleSummary[] }[] => {
  const sorted = sortArticles(articles);
  const sections: { category: HelpCategory | null; articles: HelpArticleSummary[] }[] = [];
  for (const category of [...categories].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const list = sorted.filter((a) => a.categoryId === category.id);
    if (list.length) sections.push({ category, articles: list });
  }
  const rest = sorted.filter((a) => !a.categoryId || !categories.some((c) => c.id === a.categoryId));
  if (rest.length) sections.push({ category: null, articles: rest });
  return sections;
};

/** Der empfohlene Einstieg: erster Artikel der ersten Kategorie (null ohne Inhalte). */
export const startArticle = (categories: HelpCategory[], articles: HelpArticleSummary[]): HelpArticleSummary | null => {
  const sections = groupArticlesByCategory(categories, articles);
  return sections[0]?.articles[0] ?? null;
};

/** Zielgruppe einer Kategorie (null/unbekannt → Standard). PURE. */
export const audienceOfCategory = (category: HelpCategory | null | undefined, defaultAudience: string): string => category?.audience || defaultAudience;

export interface HelpAudienceGroup {
  audience: HelpAudience;
  sections: { category: HelpCategory | null; articles: HelpArticleSummary[] }[];
  /** Artikel anderer Zielgruppen, die hier zusätzlich erscheinen (article.audiences) */
  extra: HelpArticleSummary[];
  count: number;
}

/**
 * Zielgruppe → Kategorien (mit Artikeln) + zusätzliche Artikel. Zielgruppen ohne Inhalt fallen weg.
 * Ein Artikel zählt in seiner Heimat-Zielgruppe (Kategorie) und in allen `audiences`. PURE.
 */
export const groupByAudience = (
  audiences: HelpAudience[],
  categories: HelpCategory[],
  articles: HelpArticleSummary[],
  defaultAudience: string,
): HelpAudienceGroup[] => {
  const sorted = sortArticles(articles);
  const groups: HelpAudienceGroup[] = [];
  for (const audience of [...(audiences ?? [])].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const cats = [...categories].filter((c) => audienceOfCategory(c, defaultAudience) === audience.slug).sort((a, b) => a.sortOrder - b.sortOrder);
    const sections: HelpAudienceGroup['sections'] = [];
    const seen = new Set<string>();
    for (const category of cats) {
      const list = sorted.filter((a) => a.categoryId === category.id);
      if (list.length) { sections.push({ category, articles: list }); list.forEach((a) => seen.add(a.id)); }
    }
    const extra = sorted.filter((a) => !seen.has(a.id) && (a.audiences ?? []).includes(audience.slug));
    const count = seen.size + extra.length;
    if (count > 0) groups.push({ audience, sections, extra, count });
  }
  return groups;
};

/** Lucide-Iconname aus dem Katalog → bekannter Schlüssel (unbekannt → 'life-buoy'). PURE. */
export const AUDIENCE_ICON_KEYS = ['target', 'trophy', 'users', 'map-pin', 'shopping-bag', 'network', 'life-buoy'] as const;
export type AudienceIconKey = (typeof AUDIENCE_ICON_KEYS)[number];
export const audienceIconKey = (icon: string | null | undefined): AudienceIconKey => (AUDIENCE_ICON_KEYS.includes(icon as AudienceIconKey) ? (icon as AudienceIconKey) : 'life-buoy');
